/**
 * This is a file containing constants for all of the screen names. In most cases, we should use the routes for
 * navigation. But there are situations where we may need to access screen names directly.
 */
import type DeepValueOf from './types/utils/DeepValueOf';

const PROTECTED_SCREENS = {
  HOME: 'Home',
} as const;

const SCREENS = {
  ...PROTECTED_SCREENS,
  NOT_FOUND: 'not-found',
  // Public screens
  INITIAL: 'Initial',
  AUTH: 'Auth',
  FORGOT_PASSWORD: 'ForgotPassword',
  FORCE_UPDATE: 'ForceUpdate',
  // Shared by the public and signed-in root stacks
  ADD_FRIEND: 'AddFriend',

  ONBOARDING: {
    TERMS: 'Onboarding_Terms',
    DISPLAY_NAME: 'Onboarding_DisplayName',
  },

  RIGHT_MODAL: {
    BADGES: 'Badges',
    DRINKING_SESSION: 'DrinkingSession',
    SETTINGS: 'Settings',
    PROFILE: 'Profile',
    SOCIAL: 'Social',
    STATISTICS: 'Statistics',
  },

  BADGES: {
    ROOT: 'Badges_Root',
  },

  DAY_OVERVIEW: {
    ROOT: 'DayOverview_Root',
  },

  DRINKING_SESSION: {
    ROOT: 'DrinkingSession_Root',
    LIVE: 'DrinkingSession_Live',
    EDIT: 'DrinkingSession_Edit',
    SESSION_DATE_SCREEN: 'DrinkingSession_SessionDateScreen',
    SESSION_NAME_SCREEN: 'DrinkingSession_SessionNameScreen',
    SESSION_NOTE_SCREEN: 'DrinkingSession_SessionNoteScreen',
    SESSION_TIMEZONE_SCREEN: 'DrinkingSession_SessionTimezoneScreen',
    SUMMARY: 'DrinkingSession_Summary',
  },

  SETTINGS: {
    ROOT: 'Settings_Root',
    APP_SHARE: 'Settings_AppShare',

    ACCOUNT: {
      ROOT: 'Settings_Account',
      USER_NAME: 'Settings_UserName',
      DISPLAY_NAME: 'Settings_DisplayName',
      EMAIL: 'Settings_Email',
      PASSWORD: 'Settings_Password',
      CONNECTED_ACCOUNTS: 'Settings_ConnectedAccounts',
      TIMEZONE: 'Settings_Timezone',
      TIMEZONE_SELECT: 'Settings_Timezone_Select',
      // ...
    },

    PREFERENCES: {
      ROOT: 'Settings_Preferences',
      LANGUAGE: 'Settings_Preferences_Language',
      THEME: 'Settings_Preferences_Theme',
      FIRST_DAY_OF_WEEK: 'Settings_Preferences_FirstDayOfWeek',
      UNITS_TO_COLORS: 'Settings_Preferences_UnitsToColors',
      DRINKS_TO_UNITS: 'Settings_Preferences_DrinksToUnits',
      COLOR_PALETTE: 'Settings_Preferences_ColorPalette',
      COLOR_PALETTE_CUSTOM: 'Settings_Preferences_ColorPalette_Custom',
    },

    NOTIFICATIONS: 'Settings_Notifications',

    PRIVACY: {
      ROOT: 'Settings_Privacy',
      BLOCKED_USERS: 'Settings_Privacy_BlockedUsers',
    },

    ADMIN: {
      ROOT: 'Settings_Admin',
      FEEDBACK: 'Settings_Admin_Feedback',
      BUGS: 'Settings_Admin_Bugs',
    },

    ABOUT: 'Settings_About',
    SUPPORT: 'Settings_Support',
    HELP: 'Settings_Help',
    MANAGE_SUBSCRIPTION: 'Settings_ManageSubscription',
    TERMS_OF_SERVICE: 'Settings_TermsOfService',
    PRIVACY_POLICY: 'Settings_PrivacyPolicy',
    SUBSCRIPTION_TERMS: 'Settings_SubscriptionTerms',
    REPORT_BUG: 'Settings_ReportBug',
    FEEDBACK: 'Settings_Feedback',
    MANAGE_ACCOUNT: 'Settings_ManageAccount',
    DELETE: 'Settings_Delete',
  },

  PROFILE: {
    ROOT: 'Profile_Root',
    FRIENDS_FRIENDS: 'Profile_FriendsFriends',
    REPORT_USER: 'Profile_ReportUser',
  },

  SESSIONS_CALENDAR: {
    FULLSCREEN: 'SessionsCalendar_Fullscreen',
  },

  SOCIAL: {
    ROOT: 'Social_Root',
    FRIEND_LIST: 'Social_FriendList',
    FRIEND_REQUESTS: 'Social_FriendRequests',
    ADD_FRIENDS: 'Social_AddFriends',
  },

  STATISTICS: {
    ROOT: 'Statistics_Root',
  },
} as const;

type Screen = DeepValueOf<typeof SCREENS>;

export default SCREENS;
export {PROTECTED_SCREENS};
export type {Screen};
